import type { ButtonHTMLAttributes, ReactNode, SelectHTMLAttributes, InputHTMLAttributes } from 'react';
import { useId } from 'react';

// --- Button --------------------------------------------------------------------
type Variant = 'default' | 'primary' | 'danger' | 'ghost';
export function Button({
  variant = 'default',
  size,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' }) {
  const cls = ['btn', variant !== 'default' ? variant : '', size ?? '', className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <button className={cls} {...rest}>
      {children}
    </button>
  );
}

// --- Field wrapper -------------------------------------------------------------
export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="field">
      {label && <label htmlFor={htmlFor}>{label}</label>}
      {children}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="error-text">{error}</span>}
    </div>
  );
}

// Labelled text input that wires up id + aria-invalid for accessibility.
export function TextField({
  label,
  hint,
  error,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string }) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id}>
      <input id={id} aria-invalid={error ? 'true' : undefined} {...rest} />
    </Field>
  );
}

export function SelectField({
  label,
  hint,
  error,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id}>
      <select id={id} aria-invalid={error ? 'true' : undefined} {...rest}>
        {children}
      </select>
    </Field>
  );
}

// --- Status dot ----------------------------------------------------------------
const STATUS_LABEL: Record<string, string> = {
  online: 'Online',
  offline: 'Offline',
  never_paired: 'Not paired',
};
export function StatusDot({ status }: { status: 'online' | 'offline' | 'never_paired' }) {
  return (
    <span className={`status-dot ${status}`} aria-label={STATUS_LABEL[status]}>
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

// --- Chip ----------------------------------------------------------------------
export function Chip({
  children,
  onRemove,
  selected,
  onClick,
}: {
  children: ReactNode;
  onRemove?: () => void;
  selected?: boolean;
  onClick?: () => void;
}) {
  const cls = ['chip', onRemove ? 'removable' : '', onClick ? 'selectable' : '', selected ? 'selected' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <span className={cls} onClick={onClick}>
      {children}
      {onRemove && (
        <button
          type="button"
          aria-label="Remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          ×
        </button>
      )}
    </span>
  );
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'ok' | 'warn' | 'info' | 'neutral' }) {
  return <span className={`badge ${tone}`}>{children}</span>;
}

// --- Meter ---------------------------------------------------------------------
export function Meter({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const tone = pct >= 90 ? 'danger' : pct >= 75 ? 'warn' : '';
  return (
    <div className={`meter ${tone}`} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

// --- Empty / loading / error ---------------------------------------------------
export function EmptyState({
  emoji,
  icon,
  title,
  children,
}: {
  /** Legacy single-glyph header. Ignored when `icon` is given. */
  emoji?: string;
  /** Inline SVG header — prefer this over `emoji` for new empty states (no icon fonts). */
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty">
      {icon ? <div className="empty-icon-wrap">{icon}</div> : <div className="emoji">{emoji}</div>}
      <h3>{title}</h3>
      {children}
    </div>
  );
}

export function Loading({ label }: { label?: string }) {
  return (
    <div className="loading">
      <span className="spinner" />
      {label && <div className="mt-2 text-dim">{label}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="error-state">
      <p>{message}</p>
      {onRetry && (
        <Button onClick={onRetry} size="sm">
          Retry
        </Button>
      )}
    </div>
  );
}
